/**
 * The PAC intro-call chart note — resource shape and derivations shared by the
 * Orbit card and the bots that create it.
 *
 * The note is a `Communication` categorised `ci-intro-call-note`, deliberately
 * distinct from `standalone-note` so it is addressable in queries, timeline
 * filters and the PAC access policy.
 *
 * `Communication.status` carries draft state: `in-progress` while the rep works
 * the call, `completed` once they hit Save to chart.
 */

import { Communication } from '@medplum/fhirtypes';
import { PAC_INTRO_CALL_ANSWERS_PAYLOAD_ID, CommunicationCategory } from './constants';
import { GOAL_SHORT_LABELS, POSITIVE_OUTCOMES, PacOutcome } from './patient-access-script';

/**
 * Which script the card runs. `zoom` and `inbound` are the two intro-call
 * openings; `pn-consultation` is the Patient Navigator consultation (Intake 1.1),
 * which shares this note category and payload for now and is told apart here.
 */
export type PacVisitType = 'zoom' | 'inbound' | 'pn-consultation';

/** The visit type a note must carry to render as the PN consultation card. */
export const PN_CONSULTATION_VISIT_TYPE: PacVisitType = 'pn-consultation';

const VISIT_TYPES: readonly PacVisitType[] = ['zoom', 'inbound', PN_CONSULTATION_VISIT_TYPE];

/**
 * The procedure date a patient agreed to on a call — a recorded intent, not a
 * booking; the CC team schedules once payment completes. The physician is
 * carried because the block belongs to one.
 */
export interface PacProcedureDate {
  /** The day, as an ISO date. */
  start: string;
  physicianReference: string;
  physicianName?: string;
  locationName?: string;
}

export interface PacIntroCallSlotSelection {
  start: string;
  end: string;
  practitionerReference: string;
  practitionerName?: string;
  /** Slot held while the rep talks, released or booked at the close. */
  slotId?: string;
}

/**
 * What every patient-access call note carries, whichever script it runs.
 *
 * The shared card spine (`PatientAccessCallCard`, `usePatientAccessCallDraft`) is typed
 * against this rather than against one script's answers, so a second script —
 * the warm hand-off — reuses the spine without widening it. Each script extends
 * this with whatever it alone records.
 */
export interface PatientAccessCallAnswers {
  /** Keyed by widget id. Checkbox → boolean, select/chips → string, multiChips → string[]. */
  values: Record<string, string | string[] | boolean | undefined>;
}

export interface PacIntroCallAnswers extends PatientAccessCallAnswers {
  visitType: PacVisitType;
  /** Intro call only: the consultation held and booked on the call — PN on the PAC card, NP on the legacy one. */
  slot?: PacIntroCallSlotSelection;
  /** PN consultation only: the procedure date the patient agreed to, from the site's board. */
  procedureDate?: PacProcedureDate;
}

/** Whether a note on this category is the PN consultation rather than an intro call. */
export function isPnConsultationAnswers(answers: Pick<PacIntroCallAnswers, 'visitType'>): boolean {
  return answers.visitType === PN_CONSULTATION_VISIT_TYPE;
}

/* -------------------------------------------------------------------------
   Shared by every call-note module — intro call, warm hand-off, PN
   consultation. One implementation, so a fix to how answers are read lands on
   all three cards.
   ------------------------------------------------------------------------- */

/** A non-empty string answer, or undefined. */
export function answerString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/** A multi-chip answer, tolerating anything that is not a string array. */
export function answerStrings(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];
}

/**
 * The raw answers object off a note's payload, or undefined when absent or
 * malformed. Never throws — a card that renders blank is recoverable, a card
 * that crashes the timeline is not. Callers normalise the shape for their script.
 */
export function readAnswersPayload<T>(
  communication: Pick<Communication, 'payload'>,
  payloadId: string
): Partial<T> | undefined {
  const raw = communication.payload?.find((entry) => entry.id === payloadId)?.contentString;
  if (!raw) return undefined;

  try {
    return JSON.parse(raw) as Partial<T>;
  } catch {
    return undefined;
  }
}

/** True once any value is recorded — a ticked checkbox, a chosen chip, a non-empty list. */
export function hasAnyRecordedValue(values: PatientAccessCallAnswers['values']): boolean {
  return Object.values(values).some((value) => {
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'string') return value.length > 0;
    return Boolean(value);
  });
}

/**
 * The collapsed preview line shared by the scripts that record a likelihood:
 * outcome · likelihood · barrier. A bare likelihood adjective reads oddly on
 * its own, so it gains the word "likelihood" when no barrier follows it.
 */
export function buildOutcomeLikelihoodPreview(
  values: PatientAccessCallAnswers['values'],
  isPositive: (outcome: string | undefined) => boolean
): { outcome?: string; detail: string; positive: boolean } {
  const outcome = answerString(values.close);
  const likelihood = answerString(values.likelihood);
  const barriers = answerStrings(values.barrier);

  const parts: string[] = [];
  if (likelihood) {
    parts.push(barriers.length > 0 ? likelihood : `${likelihood} likelihood`);
  }
  parts.push(...barriers);

  return { outcome, detail: parts.join(' · '), positive: isPositive(outcome) };
}

export const EMPTY_CI_INTRO_CALL_ANSWERS: PacIntroCallAnswers = { visitType: 'zoom', values: {} };

export function isCiIntroCallNote(communication: Pick<Communication, 'category'>): boolean {
  return Boolean(
    communication.category?.some((category) =>
      category.coding?.some((coding) => coding.code === CommunicationCategory.PAC_INTRO_CALL_NOTE)
    )
  );
}

/** A note is a draft until the rep saves it to the chart. */
export function isCiIntroCallDraft(communication: Pick<Communication, 'status'>): boolean {
  return communication.status !== 'completed';
}

/**
 * The newest consultation call note the rep actually **saved to the chart** —
 * not simply the newest note.
 *
 * A patient accumulates one Communication per attempt at the call card, and
 * abandoned attempts stay as `in-progress` drafts with empty answers. Taking
 * the newest by `sent` therefore picks a blank draft over a completed note
 * sitting seconds behind it, and the panel silently reports no outcome for a
 * call that was recorded. Found on staging, not by the suite: one patient had
 * three notes, the newest two empty drafts and the real one third.
 *
 * A draft is by definition not yet recorded, so it supplies neither the outcome
 * nor the Service/Location fallback — matching the timeline card, which only
 * renders an outcome once the note is saved.
 *
 * Lives here rather than in provider-app so the send-sms-message bot resolves
 * the recorded market location from exactly the note the panel displays.
 *
 * Assumes the caller searched with `_sort: '-sent'`.
 */
export function pickSavedCallNote(notes: Communication[]): Communication | undefined {
  return notes.filter(isCiIntroCallNote).find((note) => !isCiIntroCallDraft(note));
}

/**
 * Reads the structured answers off the note. Returns empty answers rather than
 * throwing on malformed JSON — a card that renders blank is recoverable, a
 * card that crashes the timeline is not.
 */
export function parseCiIntroCallAnswers(communication: Pick<Communication, 'payload'>): PacIntroCallAnswers {
  const parsed = readAnswersPayload<PacIntroCallAnswers>(communication, PAC_INTRO_CALL_ANSWERS_PAYLOAD_ID);
  if (!parsed) return { ...EMPTY_CI_INTRO_CALL_ANSWERS };

  return {
    // An unknown visit type reads as the scheduled opening, as it always has.
    visitType: VISIT_TYPES.includes(parsed.visitType as PacVisitType) ? (parsed.visitType as PacVisitType) : 'zoom',
    values: parsed.values ?? {},
    ...(parsed.slot ? { slot: parsed.slot } : {}),
    ...(parsed.procedureDate ? { procedureDate: parsed.procedureDate } : {}),
  };
}

/** True once the rep has answered anything at all. Drives the unworked-draft state. */
export function hasAnyAnswer(answers: PacIntroCallAnswers): boolean {
  return Boolean(answers.slot) || Boolean(answers.procedureDate) || hasAnyRecordedValue(answers.values);
}

export function getOutcome(answers: PacIntroCallAnswers): PacOutcome | undefined {
  return answerString(answers.values.close) as PacOutcome | undefined;
}

/** A positive close renders the check glyph; anything else renders the warning glyph. */
export function isPositiveOutcome(outcome: string | undefined): boolean {
  return Boolean(outcome && (POSITIVE_OUTCOMES as readonly string[]).includes(outcome));
}

/**
 * The collapsed preview line: outcome · likelihood · barrier.
 *
 * `activity-timeline.mdx` collapses a Call to duration + outcome, but call
 * duration is not captured here, so the line carries the two internal reads
 * instead. A bare likelihood adjective reads oddly on its own, so it gains the
 * word "likelihood" when no barrier follows it.
 */
export function buildCiIntroCallPreview(answers: PacIntroCallAnswers): {
  outcome?: string;
  detail: string;
  positive: boolean;
} {
  return buildOutcomeLikelihoodPreview(answers.values, isPositiveOutcome);
}

/**
 * Human-readable summary stored on the `text-content` payload, so timeline
 * search and any generic Communication renderer show something meaningful.
 * Deliberately terse — the card itself is the full record.
 */
export function buildCiIntroCallSummary(answers: PacIntroCallAnswers): string {
  const lines: string[] = ['Intro call'];

  const procedure = answerString(answers.values.procedure);
  if (procedure) lines.push(`Procedure: ${procedure}`);

  const goal = answerString(answers.values.wn_main);
  if (goal) lines.push(`Goal: ${GOAL_SHORT_LABELS[goal] ?? goal}`);

  const location = answerString(answers.values.loc);
  if (location) lines.push(`Location: ${location}`);

  const payment = answerString(answers.values.pay);
  if (payment) lines.push(`Payment: ${payment}`);

  if (answers.slot) {
    const withName = answers.slot.practitionerName ? ` with ${answers.slot.practitionerName}` : '';
    lines.push(`Consultation booked: ${answers.slot.start}${withName}`);
  }

  const outcome = getOutcome(answers);
  if (outcome) lines.push(`Outcome: ${outcome}`);

  const likelihood = answerString(answers.values.likelihood);
  if (likelihood) lines.push(`Likelihood: ${likelihood}`);

  const barriers = answerStrings(answers.values.barrier);
  if (barriers.length > 0) lines.push(`Barriers: ${barriers.join(', ')}`);

  return lines.join('\n');
}
